// # Sign-In Page Component
import React, { useState, useEffect } from 'react';
import './signin.css';  // # Import the CSS for styling
import { auth, functions } from '../../auth/firebase';  // # Import Firebase auth and functions
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';  // # Import httpsCallable
import AuthHeader from '../components/AuthHeader';  // # Import AuthHeader
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { sendResetPasswordEmail } from '../../utils/reset_password';
import '../../utils/reset_password.css';
import ErrorMessage from '../components/ErrorMessage';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import loginOutManager from '../../auth/login_out_manager';  // # Import login manager
import Loading from '../../components/common/Loading';  // # Import Loading component
import sessionManager from '../../auth/session';  // # Import session manager
import Cookies from 'js-cookie'; // Add direct Cookies import

function Signin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { initializeUserSession, isAuthenticated, resetAuth, setDeviceLimitReached } = useAuthStore();
  const [email, setEmail] = useState(location.state?.email || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || null);

  // # Cookie management functions
  const hasValidAuthCookies = () => {
    return !!(
      Cookies.get('authToken') &&
      Cookies.get('deviceId') &&
      Cookies.get('sessionActive')
    );
  };

  const setAuthTokens = async (idToken, deviceId) => {
    try {
      // # Set auth token cookie
      Cookies.set('authToken', idToken, {
        expires: 7, // 7 days
        secure: true,
        sameSite: 'strict',
        path: '/'
      });

      // # Set device ID cookie
      Cookies.set('deviceId', deviceId, {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        path: '/'
      });

      // # Set session active cookie
      Cookies.set('sessionActive', 'true', {
        expires: 7,
        secure: true,
        sameSite: 'strict',
        path: '/'
      });

      return true;
    } catch (error) {
      console.error('Error setting auth cookies:', error);
      return false;
    }
  };

  const clearAuthCookies = async () => {
    try {
      Cookies.remove('authToken', { path: '/' });
      Cookies.remove('deviceId', { path: '/' });
      Cookies.remove('sessionActive', { path: '/' });
      return true;
    } catch (error) {
      console.error('Error clearing auth cookies:', error);
      return false;
    }
  };

  // # Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !error?.type === 'device_limit' && !location.pathname.includes('welcome')) {
      // # Add debug logging
      console.log('Signin: Redirecting authenticated user:', {
        isAuthenticated,
        error,
        pathname: location.pathname
      });
      navigate('/dashboard', { 
        replace: true,
        state: { 
          isAuthenticated: true,
          timestamp: Date.now()
        }
      });
    }
  }, [isAuthenticated, navigate, error, location]);

  // # Show welcome message if redirected from signup
  useEffect(() => {
    if (location.state?.message) {
      alert(location.state.message);
      // # Clear the message after showing it
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // # Show success message if redirected from signup
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // # Clear the message from history
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // # Clear success message after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // # Check for existing valid session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      if (hasValidAuthCookies()) {
        try {
          // # Attempt to refresh tokens
          if (auth.currentUser) {
            const idToken = await auth.currentUser.getIdToken(true);
            const deviceId = Cookies.get('deviceId');
            if (idToken && deviceId) {
              const success = await setAuthTokens(idToken, deviceId);
              if (success) {
                // # Initialize session and redirect
                await sessionManager.initializeSession(auth.currentUser, false);
                navigate('/dashboard');
                return;
              }
            }
          }
        } catch (error) {
          console.error('Error checking existing session:', error);
          // # Clear invalid session
          await handleSignOut();
        }
      }
    };

    checkExistingSession();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await Promise.all([
        auth.signOut(),
        sessionManager.clearSession(),
        clearAuthCookies(),
        resetAuth()
      ]);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // # Check user status (banned or suspended)
  const checkUserStatus = async (uid) => {
    try {
      // # Call the checkUserStatus function
      const checkStatus = httpsCallable(functions, 'checkUserStatus');
      const result = await checkStatus({ uid });
      
      return result.data;
    } catch (error) {
      console.error('Error checking user status:', error);
      return { isBanned: false, isSuspended: false };
    }
  };

  // # Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDeviceLimitReached(false); // Reset the flag

    try {
      // # Sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // # Check if user is banned or suspended
      const userStatus = await checkUserStatus(user.uid);
      
      if (userStatus.isBanned) {
        // # User is banned, show error and sign out
        setError({
          type: 'banned',
          message: 'Your account has been banned',
          details: [
            `Reason: ${userStatus.banDetails?.banReason || 'Violation of terms of service'}`,
            `Banned on: ${formatDate(userStatus.banDetails?.banTime)}`,
            'If you believe this is an error, please contact support'
          ],
          links: [
            {
              text: 'Contact Support',
              url: 'mailto:wearetemporary@gmail.com'
            }
          ]
        });
        
        // # Sign out the user
        await handleSignOut();
        setLoading(false);
        return;
      }
      
      if (userStatus.isSuspended) {
        // # User is suspended, show error and sign out
        setError({
          type: 'suspended',
          message: 'Your account has been temporarily suspended',
          details: [
            `Reason: ${userStatus.suspendDetails?.suspendReason || 'Violation of community guidelines'}`,
            `Suspended on: ${formatDate(userStatus.suspendDetails?.suspendedAt)}`,
            `Suspension ends: ${formatDate(userStatus.suspensionEnd)}`,
            'You will be able to access your account after the suspension period ends'
          ],
          links: [
            {
              text: 'Contact Support',
              url: 'mailto:wearetemporary@gmail.com'
            }
          ]
        });
        
        // # Sign out the user
        await handleSignOut();
        setLoading(false);
        return;
      }

      // # Get tokens
      const idToken = await user.getIdToken();
      
      // # Generate device ID
      const deviceId = btoa(
        JSON.stringify({
          platform: navigator.platform,
          userAgent: navigator.userAgent,
          cores: navigator.hardwareConcurrency,
          timestamp: Date.now()
        })
      ).replace(/[/+=]/g, '').substring(0, 32);
      
      // # Set auth cookies
      const cookieResult = await setAuthTokens(idToken, deviceId);
      if (!cookieResult) {
        throw new Error('Failed to set authentication cookies');
      }

      // # Record login activity
      try {
        await loginOutManager.recordSignInActivity(user.uid);
      } catch (activityError) {
        // # Handle device limit error
        if (activityError.code === 'failed-precondition' && 
            activityError.message.includes('Maximum device limit')) {
          setError({
            type: 'device_limit',
            message: 'You have reached the maximum number of devices (3).',
            details: [
              'Dear user, please note that you can only be signed in on 3 devices at a time',
              'This helps us maintain our security rules and fair use policy',
              'Please review our Terms of Service and Privacy Policy for more details',
              <React.Fragment key="email">
                Feel free to contact us at <a href="mailto:wearetemporary@gmail.com">wearetempu@gmail.com</a> if you have any inquiries
              </React.Fragment>
            ],
            links: [
              {
                text: 'Terms of Service',
                url: '/terms'
              },
              {
                text: 'Privacy Policy',
                url: '/privacy'
              }
            ]
          });
          setLoading(false);
          return;
        }

        // # For other activity errors, sign out and show generic error
        console.error('Activity error:', activityError);
        await handleSignOut();
        
        setError({
          type: 'activity',
          message: 'Unable to sign in at this time',
          details: [
            'Our servers are experiencing high load',
            'Please try again in a few minutes',
            'If the problem persists, contact support'
          ]
        });
        setLoading(false);
        return;
      }

      // # Initialize session
      await sessionManager.initializeSession(user, true);

      // # Get token claims to check admin status
      const idTokenResult = await user.getIdTokenResult();
      const isAdmin = idTokenResult.claims.admin === true;

      // # Navigate to welcome screen with correct destination
      const destination = '/welcome';
      const finalDestination = isAdmin ? '/admin' : (location.state?.from || '/dashboard');
      
      navigate(destination, {
        replace: true,
        state: {
          isAuthenticated: true,
          timestamp: Date.now(),
          bypassLoading: true,
          redirectTo: finalDestination,
          isAdmin
        }
      });

    } catch (error) {
      console.error('Sign in error:', error);
      let errorMessage = '';
      
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        default:
          errorMessage = 'An error occurred during sign in';
      }
      
      setError({
        type: 'auth',
        message: errorMessage,
        details: [
          'Please check your credentials and try again',
          error.code === 'auth/wrong-password' ? 'If you forgot your password, use the "Forgot Password" link below' : null,
          error.code === 'auth/user-not-found' ? 'If you don\'t have an account, please sign up' : null
        ].filter(Boolean)
      });
    } finally {
      setLoading(false);
    }
  };

  // # Handle password reset with better error handling
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage(null);

    // # Use the email from the signin form if reset email is empty
    const emailToReset = resetEmail || email;

    if (!emailToReset) {
      setResetMessage({
        type: 'error',
        message: 'Please enter an email address'
      });
      setResetLoading(false);
      return;
    }

    try {
      const result = await sendResetPasswordEmail(emailToReset);
      setResetMessage({
        type: 'success',
        message: 'Password reset link sent! Please check your email'
      });

      // Clear email and close modal after success
      setTimeout(() => {
        setShowResetModal(false);
        setResetEmail('');
        setResetMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Reset password error:', error);
      setResetMessage({
        type: 'error',
        message: 'Failed to send reset email. Please try again'
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div>
      <AuthHeader />  {/* # Add AuthHeader */}
      {loading && <Loading message="Signing in..." />}
      <div className="sgnn_signin-container">
        <div className="sgnn_signin-image-section" />
        <div className="sgnn_signin-form-section">
          <h2>Welcome Back</h2>
          <form onSubmit={handleSubmit}>
            <div className="sgnn_form-group">
              <div className="sgnn_input-group">
                <label className="sgnn_input-label">Email</label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={error?.type === 'email' ? 'sgnn_error' : ''}
                />
                {error?.type === 'email' && (
                  <div className="sgnn_error-container">
                    <span className="sgnn_field-error">{error.message}</span>
                    {error.details && (
                      <ul className="sgnn_error-details">
                        {error.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              
              <div className="sgnn_input-group">
                <label className="sgnn_input-label">Password</label>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={error?.type === 'password' ? 'sgnn_error' : ''}
                />
                {error?.type === 'password' && (
                  <div className="sgnn_error-container">
                    <span className="sgnn_field-error">{error.message}</span>
                    {error.details && (
                      <ul className="sgnn_error-details">
                        {error.details.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Show general errors with details */}
            {error && !['email', 'password'].includes(error.type) && (
              <div className="sgnn_form-error">
                <div className="sgnn_error-message">{error.message}</div>
                {error.details && (
                  <ul className="sgnn_error-details">
                    {error.details.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                )}
                {error.links && (
                  <div className="sgnn_error-links">
                    {error.links.map((link, index) => (
                      <React.Fragment key={link.url}>
                        <a href={link.url} className="sgnn_error-link">{link.text}</a>
                        {index < error.links.length - 1 && <span className="sgnn_link-separator">|</span>}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <div className="sgnn_forgot-password-link">
            <button 
              onClick={() => setShowResetModal(true)}
              className="sgnn_text-button"
            >
              Forgot Password?
            </button>
          </div>
        </div>
        {showResetModal && (
          <div className="sgnn_reset-password-modal">
            <div className="sgnn_reset-password-content">
              <div className="sgnn_reset-password-header">
                <h3>Reset Password</h3>
                <button 
                  className="sgnn_close-button"
                  onClick={() => {
                    setShowResetModal(false);
                    setResetEmail('');
                    setResetMessage(null);
                  }}
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleResetPassword} className="sgnn_reset-password-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
                <button type="submit" disabled={resetLoading}>
                  {resetLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
              {resetMessage && (
                <div className={`sgnn_reset-message ${resetMessage.type === 'success' ? 'sgnn_success' : 'sgnn_error'}`}>
                  {resetMessage.message}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Signin;
